import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

const BASE_URL = `${environment.apiHost}/api/v1/application-configurations`;
const TOKEN_KEY = 'auth-token';

export interface ApplicationChannelConfiguration {
  applicationChannelId?: number;
  channelCode: string;
  enabled: boolean;
}

export interface ApplicationCapabilityConfiguration {
  applicationCapabilityId?: number;
  capabilityCode: string;
  capabilityName: string;
  enabled: boolean;
  displayOrder: number;
}

export interface ApplicationConfiguration {
  applicationId?: number;
  applicationName: string;
  applicationCode: string;
  enabled: boolean;
  integrationType?: string | null;
  integrationProfile?: string | null;
  channels: ApplicationChannelConfiguration[];
  capabilities: ApplicationCapabilityConfiguration[];
  createdAt?: string;
  updatedAt?: string;
}

export interface AutonomousPolicy {
  enabled: boolean; hermesMode: string; severities: string[]; eventTypes: string[];
  cooldownMinutes: number; maxActionsPerIncident: number; maxActionsPerDeviceHour: number;
  maxRetries: number; escalateAfterRepeatedFailure: boolean; requireVerification: boolean;
  requiresApproval: string[];
}
export interface AutonomousIncident {
  id: number; startedAt: string; eventType: string; severity: string; device: string;
  probableCause?: string; recommendedAction?: string; actionCount: number;
  verificationStatus?: string; status: string; escalationReason?: string;
}
export interface AutonomousApproval {
  id: number; incidentId: number; action: string; target: string; risk?: string; requestedAt: string;
}
export interface WhatsAppRoute {
  providerCode: string; displayName: string; enabled: boolean;
  routeType: 'HTTP'|'INTERNAL';
  matchMode: 'LOCAL_ALLOWLIST'|'REMOTE_LOOKUP'|'ALL';
  eligibilityUrl: string|null; eligibilityResponseField: string|null;
  messageUrl: string|null; responseField: string;
  authType: 'NONE'|'BASIC'|'BEARER'|'FORWARD_BEARER';
  authUsername: string|null; authSecret?: string; secretConfigured: boolean;
  timeoutMs: number; failureMessage: string; failureMode: 'ISOLATE';
  priority: number; updatedAt?: string;
}
export interface WhatsAppRouteNumber { id?: number; providerCode?: string; name: string; role: string; whatsappNumber: string; active: boolean; }

@Injectable({ providedIn: 'root' })
export class ApplicationConfigurationService {
  async list(): Promise<ApplicationConfiguration[]> {
    return this.request<ApplicationConfiguration[]>('', { method: 'GET' });
  }

  async create(application: ApplicationConfiguration): Promise<ApplicationConfiguration> {
    return this.request<ApplicationConfiguration>('', {
      method: 'POST',
      body: JSON.stringify(application),
    });
  }

  async update(application: ApplicationConfiguration): Promise<ApplicationConfiguration> {
    if (!application.applicationId) throw new Error('Application ID is required');
    return this.request<ApplicationConfiguration>(`/${application.applicationId}`, {
      method: 'PUT',
      body: JSON.stringify(application),
    });
  }

  autonomousPolicy(): Promise<AutonomousPolicy> { return this.absolute('/api/v1/nms/autonomous/policy', { method: 'GET' }); }
  saveAutonomousPolicy(policy: AutonomousPolicy): Promise<AutonomousPolicy> { return this.absolute('/api/v1/nms/autonomous/policy', { method: 'PUT', body: JSON.stringify(policy) }); }
  autonomousActivity(): Promise<AutonomousIncident[]> { return this.absolute('/api/v1/nms/autonomous/activity', { method: 'GET' }); }
  autonomousApprovals(): Promise<AutonomousApproval[]> { return this.absolute('/api/v1/nms/autonomous/approvals', { method: 'GET' }); }
  providerCapabilities(): Promise<Record<string,Record<string,boolean>>> { return this.absolute('/api/v1/nms/capabilities/provider', { method: 'GET' }); }
  decideApproval(id: number, decision: 'APPROVED'|'REJECTED'): Promise<AutonomousIncident> { return this.absolute(`/api/v1/nms/autonomous/approvals/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision }) }); }
  whatsappRoutes():Promise<WhatsAppRoute[]>{return this.absolute('/api/v1/whatsapp-routing/routes',{method:'GET'});}
  activateWhatsAppRoute(providerCode:string):Promise<WhatsAppRoute[]>{return this.absolute(`/api/v1/whatsapp-routing/routes/${encodeURIComponent(providerCode)}/activate`,{method:'PUT'});}
  saveWhatsAppRoute(value:WhatsAppRoute,isNew:boolean):Promise<WhatsAppRoute>{const path=isNew?'/api/v1/whatsapp-routing/routes':`/api/v1/whatsapp-routing/routes/${encodeURIComponent(value.providerCode)}`;return this.absolute(path,{method:isNew?'POST':'PUT',body:JSON.stringify(value)});}
  deleteWhatsAppRoute(providerCode:string):Promise<void>{return this.absolute(`/api/v1/whatsapp-routing/routes/${encodeURIComponent(providerCode)}`,{method:'DELETE'});}
  whatsappRouteNumbers(providerCode:string):Promise<WhatsAppRouteNumber[]>{return this.absolute(`/api/v1/whatsapp-routing/routes/${encodeURIComponent(providerCode)}/numbers`,{method:'GET'});}
  saveWhatsAppRouteNumber(providerCode:string,value:WhatsAppRouteNumber):Promise<WhatsAppRouteNumber>{const base=`/api/v1/whatsapp-routing/routes/${encodeURIComponent(providerCode)}/numbers`;return this.absolute(value.id?`${base}/${value.id}`:base,{method:value.id?'PUT':'POST',body:JSON.stringify(value)});}
  deleteWhatsAppRouteNumber(providerCode:string,id:number):Promise<void>{return this.absolute(`/api/v1/whatsapp-routing/routes/${encodeURIComponent(providerCode)}/numbers/${id}`,{method:'DELETE'});}

  private async absolute<T>(path: string, init: RequestInit): Promise<T> {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    const response = await fetch(`${environment.apiHost}${path}`, { ...init, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    const body: any = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.message || body?.detail || `HTTP ${response.status}`);
    return (body?.data ?? body) as T;
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    const response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const body: any = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.message || body?.detail || `HTTP ${response.status}`);
    return (body?.data ?? body) as T;
  }
}
